import React, { useEffect, useRef } from 'react';
import { Circle, Line, Arrow } from 'react-konva';
import { useTransformer } from './TransformerContext';

const Arrows = ({ type = "normal", id, isSelected, x, y, onSelect, draggable = true, ...props }) => {
    const transformerRef = useTransformer();
    const arrowRef = useRef();

    const [hoveredAnchor, setHoveredAnchor] = React.useState(null);

    const [nPoints, setNPoints] = React.useState({
        start: { x: x, y: y },
        end: { x: (x + 80), y: (y + 160) },
    });

    const [quadPoints, setQuadPoints] = React.useState({
        start: { x: x, y: y },
        control: { x: (x + 140), y: (y + 40) },
        end: { x: (x + 80), y:  (y + 160)},
    });

    const [bezierPoints, setBezierPoints] = React.useState({
        start: { x: x, y: y },
        control1: { x: (x), y: (y + 200)},
        control2: { x: (x + 280), y: (y-100)},
        end: { x: (x + 300), y: (y + 150)},
    });

    const handleDragMove = (e, points, setPoints, pointName) => {
        setPoints({
            ...points,
            [pointName]: { x: e.target.x(), y: e.target.y() }
        });
    };

    const handleCursor = (e, pointId, isEnter) => {
        const stage = e.target.getStage();
        stage.container().style.cursor = isEnter ? 'pointer' : 'default';
        setHoveredAnchor(isEnter ? pointId : null);
    };

    const renderAnchor = (point, pointName, prefix, onDragMove) => (
        <Circle
            key={prefix + pointName}
            x={point.x}
            y={point.y}
            radius={6}
            stroke="#666"
            fill={hoveredAnchor === prefix + pointName ? "#ff9900" : "#ddd"}
            strokeWidth={hoveredAnchor === prefix + pointName ? 2 : 1}
            draggable
            onDragMove={onDragMove}
            onMouseEnter={e => handleCursor(e, prefix + pointName, true)}
            onMouseLeave={e => handleCursor(e, prefix + pointName, false)}
        />
    );

    const getBezierArrowPoints = () => {
        const points = [];
        for (let t = 0; t <= 1; t += 0.05) {
            const x = Math.pow(1 - t, 3) * bezierPoints.start.x +
                3 * Math.pow(1 - t, 2) * t * bezierPoints.control1.x +
                3 * (1 - t) * Math.pow(t, 2) * bezierPoints.control2.x +
                Math.pow(t, 3) * bezierPoints.end.x;
            const y = Math.pow(1 - t, 3) * bezierPoints.start.y +
                3 * Math.pow(1 - t, 2) * t * bezierPoints.control1.y +
                3 * (1 - t) * Math.pow(t, 2) * bezierPoints.control2.y +
                Math.pow(t, 3) * bezierPoints.end.y;
            points.push(x, y);
        }
        return points;
    };

    const applyArrowTransform = () => {
        const arrow = arrowRef.current;
        if (!arrow) return;

        const scaleX = arrow.scaleX();
        const scaleY = arrow.scaleY();
        const dx = arrow.x();
        const dy = arrow.y();

        if (type === 'normal') {
            setNPoints({
                start: {
                    x: nPoints.start.x * scaleX + dx,
                    y: nPoints.start.y * scaleY + dy
                },
                end: {
                    x: nPoints.end.x * scaleX + dx,
                    y: nPoints.end.y * scaleY + dy
                }
            });
        } else if (type === 'quad') {
            setQuadPoints({
                start: {
                    x: quadPoints.start.x * scaleX + dx,
                    y: quadPoints.start.y * scaleY + dy
                },
                control: {
                    x: quadPoints.control.x * scaleX + dx,
                    y: quadPoints.control.y * scaleY + dy
                },
                end: {
                    x: quadPoints.end.x * scaleX + dx,
                    y: quadPoints.end.y * scaleY + dy
                }
            });
        } else if (type === 'bez') {
            setBezierPoints({
                start: {
                    x: bezierPoints.start.x * scaleX + dx,
                    y: bezierPoints.start.y * scaleY + dy
                },
                control1: {
                    x: bezierPoints.control1.x * scaleX + dx,
                    y: bezierPoints.control1.y * scaleY + dy
                },
                control2: {
                    x: bezierPoints.control2.x * scaleX + dx,
                    y: bezierPoints.control2.y * scaleY + dy
                },
                end: {
                    x: bezierPoints.end.x * scaleX + dx,
                    y: bezierPoints.end.y * scaleY + dy
                }
            });
        }

        // Reset transforms
        arrow.setAttrs({
            scaleX: 1,
            scaleY: 1,
            x: 0,
            y: 0,
        });
    };

    const arrowCommonProps = {
        ref: arrowRef,
        hitStrokeWidth: 24,
        draggable,
        ...props,
        onClick: (e) => { applyArrowTransform(); onSelect(e) },
        onTap: (e) => { applyArrowTransform(); onSelect(e) },
        onDragEnd: applyArrowTransform,
        onTransformEnd: applyArrowTransform,
    };

    return (
        <>
            {type === 'normal' && (
                <>
                    <Arrow
                        {...arrowCommonProps}
                        points={[
                            nPoints.start.x, nPoints.start.y,
                            nPoints.end.x, nPoints.end.y
                        ]}
                        pointerLength={10}
                        pointerWidth={10}
                        id={id}

                    />
                    {isSelected && Object.entries(nPoints).map(([name, point]) =>
                        renderAnchor(point, name, 'norm-', e =>
                            handleDragMove(e, nPoints, setNPoints, name)
                        )
                    )}
                </>
            )}

            {type === 'quad' && (
                <>
                    <Arrow
                        {...arrowCommonProps}
                        points={[
                            quadPoints.start.x, quadPoints.start.y,
                            quadPoints.control.x, quadPoints.control.y,
                            quadPoints.end.x, quadPoints.end.y
                        ]}
                        tension={0.5}
                        pointerLength={10}
                        pointerWidth={10}
                        id={id}

                    />
                    {isSelected && (
                        <>
                            <Line
                                points={[
                                    quadPoints.start.x, quadPoints.start.y,
                                    quadPoints.control.x, quadPoints.control.y,
                                    quadPoints.end.x, quadPoints.end.y
                                ]}
                                dash={[10, 10]}
                                stroke="#666"
                                strokeWidth={2}
                                opacity={0.5}
                            />
                            {Object.entries(quadPoints).map(([name, point]) =>
                                renderAnchor(point, name, 'quad-', e =>
                                    handleDragMove(e, quadPoints, setQuadPoints, name)
                                )
                            )}
                        </>
                    )}
                </>
            )}

            {type === 'bez' && (
                <>
                    <Arrow
                        {...arrowCommonProps}
                        points={getBezierArrowPoints()}
                        pointerLength={10}
                        pointerWidth={10}
                        tension={0}
                        id={id}

                    />
                    {isSelected && (
                        <>
                            <Line
                                points={[
                                    bezierPoints.start.x, bezierPoints.start.y,
                                    bezierPoints.control1.x, bezierPoints.control1.y,
                                    bezierPoints.control2.x, bezierPoints.control2.y,
                                    bezierPoints.end.x, bezierPoints.end.y
                                ]}
                                dash={[10, 10]}
                                stroke="#666"
                                strokeWidth={2}
                                opacity={0.5}
                                hitStrokeWidth={20}
                            />
                            {Object.entries(bezierPoints).map(([name, point]) =>
                                renderAnchor(point, name, 'bez-', e =>
                                    handleDragMove(e, bezierPoints, setBezierPoints, name)
                                )
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default Arrows;


